import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiCreditCard,
  FiFileText,
  FiGlobe,
  FiLink,
  FiLock,
  FiMapPin,
  FiPercent,
  FiRefreshCw,
  FiRotateCcw,
  FiShield,
  FiTarget,
  FiTrash2,
} from "react-icons/fi";
import MarketingPageLayout from "../../components/marketing/MarketingPageLayout";
import "../../styles/auth.css";

const TERMS = [
  {
    id: 1,
    title: "Introduction",
    icon: FiFileText,
    body: "Software Solutions Development India (SSDI) is the developer and service provider of the FastRecovery website. This website has been developed to securely and quickly deliver legal finance vehicle-related data uploaded by banks/NBFCs/authorized legal repossession agencies to registered legal recovery agency users. SSDI is only a technology service provider and complies with the Information Technology Act, 2000, the Digital Personal Data Protection Act, 2023, and applicable payment and GST rules.",
  },
  {
    id: 2,
    title: "Data and Location",
    icon: FiMapPin,
    body: "FastRecovery delivers data uploaded by banks/authorized legal repossession agencies to registered agency users. Location data may be saved for tracking in accordance with the bank's agreement. This information is visible to the authorized bank admin, bank staff, or legal recovery admin as per the relevant case. SSDI's own admin cannot save or view any person's location, vehicle tracing, or tracking-related information in its admin panel.",
  },
  {
    id: 3,
    title: "Responsibility",
    icon: FiShield,
    body: "Responsibility for the data or any transaction shall lie solely with the bank/NBFC/authorized legal agency uploading the data. FastRecovery/SSDI provides data access and tracking services according to the selected trace package.",
  },
  {
    id: 4,
    title: "Agency Rules",
    icon: FiAlertTriangle,
    body: "The agency/user shall not threaten, abuse, harass, or use force against any person. Recovery shall be carried out only in accordance with applicable laws and RBI guidelines. The agency must have valid authorization from the bank/NBFC. In the event of illegal activities or violation of privacy, SSDI may close the account without prior notice, and no refund will be provided.",
  },
  {
    id: 5,
    title: "Account Activation and Payment",
    icon: FiCreditCard,
    body: "SSDI activates an agency's account only after receiving the required Aadhaar, PAN, and GST documents and payment for the selected package. In case of non-payment or non-compliance with the plan rules, the service may continue for only 24 hours after the prescribed deadline, after which it may be discontinued.",
  },
  {
    id: 6,
    title: "Account Reactivation",
    icon: FiRefreshCw,
    body: "If an account is closed for any reason, the account/app will be reactivated only after 24 hours from receiving permission from the SSDI admin.",
  },
  {
    id: 7,
    title: "Bank/NBFC Connection",
    icon: FiLink,
    body: "No admin panel will be connected to the data of any bank/NBFC until the relevant bank/NBFC sends the required documents to SSDI in writing via email and receives approval from SSDI.",
  },
  {
    id: 8,
    title: "Third Parties and Legal Action",
    icon: FiLock,
    body: "SSDI/FastRecovery will not provide account information or assistance to any third party based on phone calls or requests from any unregistered source. Upon receiving a court order, FIR, or legal notice, SSDI shall be authorized to provide account information or suspend the account in accordance with the law.",
  },
  {
    id: 9,
    title: "KYC Verification",
    icon: FiCheckCircle,
    body: "An agency's account is activated after reviewing the submitted KYC/documents. The agency admin shall bear full responsibility for the authenticity of documents uploaded by the agency/user. SSDI/FastRecovery shall not be responsible for verifying their authenticity.",
  },
  {
    id: 10,
    title: "Refund Policy",
    icon: FiRotateCcw,
    body: "A refund request may be submitted only within 24 hours of account activation/update. If the request is approved, the amount will be refunded online within 1–7 working days. No refund will be provided after 24 hours.",
  },
  {
    id: 11,
    title: "Account Deletion",
    icon: FiTrash2,
    body: "Users may delete or cancel their accounts at any time. The refund policy shall remain applicable even after account deletion.",
  },
  {
    id: 12,
    title: "Applicable Law and Jurisdiction",
    icon: FiGlobe,
    body: "All disputes shall be subject to the judicial jurisdiction of Delhi.",
  },
  {
    id: 13,
    title: "GST",
    icon: FiPercent,
    body: "The admin creating the account shall be responsible for paying 18% GST on the charges for the selected package.",
  },
  {
    id: 14,
    title: "Purpose of the Platform",
    icon: FiTarget,
    body: "The sole purpose of FastRecovery (SSDI) is to deliver data uploaded by banks/authorized legal agencies to ground-level working teams as quickly as possible. When a vehicle is searched for or traced, its search/trace location may be saved through the relevant admin panel for future use. During lawful recovery, users/legal recovery agents may upload inventory photos taken before and after the vehicle recovery at the site. This platform has been developed solely to facilitate the transfer of information between banks, agencies, and authorized users, and not to cause personal harm to any individual.",
  },
];

const HIGHLIGHTS = [
  { id: 10, label: "24-hour refunds" },
  { id: 12, label: "Delhi jurisdiction" },
  { id: 13, label: "18% GST" },
  { id: 2, label: "Location privacy" },
];

function TermItem({ term, open, onToggle }) {
  const Icon = term.icon;
  const panelId = `term-panel-${term.id}`;
  const triggerId = `term-trigger-${term.id}`;

  return (
    <article id={`term-${term.id}`} className={`terms-item${open ? " is-open" : ""}`}>
      <button
        type="button"
        id={triggerId}
        className="terms-item__trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="terms-item__index">{String(term.id).padStart(2, "0")}</span>
        <span className="terms-item__icon" aria-hidden>
          <Icon size={18} />
        </span>
        <span className="terms-item__title">{term.title}</span>
        <FiChevronDown className="terms-item__chevron" aria-hidden />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className="terms-item__panel"
        hidden={!open}
      >
        <p>{term.body}</p>
      </div>
    </article>
  );
}

export default function TermsPage() {
  const [openIds, setOpenIds] = useState(() => new Set([1]));

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Terms and Conditions | FastRecovery";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const jumpTo = (id) => {
    setOpenIds((prev) => new Set(prev).add(id));
    requestAnimationFrame(() => {
      document.getElementById(`term-${id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  return (
    <MarketingPageLayout>
      <header className="marketing-page__hero terms-hero">
        <p className="terms-hero__eyebrow">Legal · FastRecovery App</p>
        <h1 className="marketing-page__title">Terms and Conditions</h1>
        <p className="marketing-page__lead">
          Software Solutions Development India (SSDI) — rules for using the FastRecovery
          platform.
        </p>
        <div className="terms-highlights">
          {HIGHLIGHTS.map((h) => (
            <button
              key={h.id}
              type="button"
              className="terms-highlight"
              onClick={() => jumpTo(h.id)}
            >
              {h.label}
            </button>
          ))}
        </div>
      </header>

      <div className="terms-list">
        {TERMS.map((term) => (
          <TermItem
            key={term.id}
            term={term}
            open={openIds.has(term.id)}
            onToggle={() => toggle(term.id)}
          />
        ))}
      </div>

      <div className="marketing-page__actions">
        <Link to="/" className="primary-page-btn">
          ← Back to Login
        </Link>
      </div>
    </MarketingPageLayout>
  );
}
